import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart3, Users, TrendingDown, TrendingUp, Flame, Syringe, Utensils, Scale, Percent } from "lucide-react";

const TEAL = "#0D9488";

/** Coach-only dashboard tile: retention + avg Δweight + avg streak + activity totals. */
export default function CoachAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coach/analytics")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-6 h-40 animate-pulse" data-testid="coach-analytics-loading" />;
  }
  if (!data) return null;

  const weightIcon = data.avg_weight_change_30d !== null && data.avg_weight_change_30d < 0 ? TrendingDown : TrendingUp;
  const weightHue = data.avg_weight_change_30d !== null && data.avg_weight_change_30d < 0 ? "text-teal-700" : "text-orange-600";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-analytics-card">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Coach analytics</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-4">Across all your clients · last 30 days</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          icon={Users}
          label="Active"
          value={data.active_clients}
          hint={data.lifetime_clients > 0 ? `of ${data.lifetime_clients} lifetime` : "no history yet"}
          testid="analytics-active-clients"
        />
        <Stat
          icon={Percent}
          label="Retention"
          value={data.retention_pct !== null ? `${data.retention_pct}%` : "—"}
          hint="active / lifetime"
          testid="analytics-retention"
        />
        <Stat
          icon={weightIcon}
          label="Avg Δweight"
          value={data.avg_weight_change_30d !== null ? `${data.avg_weight_change_30d > 0 ? "+" : ""}${data.avg_weight_change_30d} lb` : "—"}
          hint="past 30d"
          hue={weightHue}
          testid="analytics-weight-delta"
        />
        <Stat
          icon={Flame}
          label="Avg streak"
          value={data.avg_supplement_streak !== null ? `${data.avg_supplement_streak}d` : "—"}
          hint="supplement top-streak per client"
          testid="analytics-avg-streak"
        />
      </div>

      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
        <Micro icon={Syringe} label="Shots" value={data.shots_last_30d} testid="analytics-shots" />
        <Micro icon={Utensils} label="Meals" value={data.meals_last_30d} testid="analytics-meals" />
        <Micro icon={Scale} label="Weigh-ins" value={data.weigh_ins_last_30d} testid="analytics-weighins" />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, hue = "text-slate-900", testid }) {
  return (
    <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/40" data-testid={testid}>
      <Icon className={`w-4 h-4 mb-1 ${hue}`} />
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <div className={`text-xl font-bold ${hue} tabular-nums mt-0.5`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Micro({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</span>
      <span className="ml-auto text-sm font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
